// apps/web/src/hooks/useTeamPerformanceReport.ts
/**
 * Team Performance report data + filter options.
 *
 * The report does NOT fetch on mount, same as Agent Performance: it runs
 * only when `run()` is called with a validated filter set, because the query
 * fans across multiple fact tables and an unbounded default window would be
 * an expensive query nobody asked for. `hasRun` drives the empty state.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface TeamPerformanceRow {
  teamId: string
  teamName: string
  assigned: number
  closed: number
  workInProgress: number
  reOpened: number
  transferToTeam: number
  transferByTeam: number
  notes: number
  responses: number
  avgResponseSeconds: number | null
  avgResolutionSeconds: number | null
  withinResponseSla: number
  withinResolutionSla: number
  responseSlaPct: number | null
  resolutionSlaPct: number | null
}

export interface TeamReportFilters {
  timezone: string
  interval: string
  startDate: string
  endDate: string
  /** HH:MM, optional — defaults to the whole day on the backend. */
  startTime: string
  endTime: string
  campaignIds: string[]
  teamIds: string[]
}

export interface FilterOption { label: string; value: string }
export interface TimezoneOption extends FilterOption { offset: string }

interface FilterOptions {
  timezones: TimezoneOption[]
  campaigns: FilterOption[]
  teams: FilterOption[]
}

/** Options for the filter panel; re-fetched as the campaign selection narrows. */
export function useTeamPerformanceFilterOptions(
  campaignIds: string[],
): { options: FilterOptions; loading: boolean } {
  const [options, setOptions] = useState<FilterOptions>({
    timezones: [], campaigns: [], teams: [],
  })
  const [loading, setLoading] = useState(true)

  const campaignKey = campaignIds.join(',')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (campaignIds.length > 0) params.set('campaignIds', campaignIds.join(','))
        const res = await api.get<FilterOptions>(
          `/reports/conversations/team-performance/filters?${params.toString()}`,
        )
        if (!cancelled) setOptions(res)
      } catch {
        if (!cancelled) setOptions({ timezones: [], campaigns: [], teams: [] })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [campaignKey])

  return { options, loading }
}

interface UseReportResult {
  rows: TeamPerformanceRow[]
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: TeamReportFilters) => Promise<void>
  reset: () => void
}

export function useTeamPerformanceReport(): UseReportResult {
  const [rows, setRows] = useState<TeamPerformanceRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: TeamReportFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        timezone: filters.timezone,
        interval: filters.interval,
        startDate: filters.startDate,
        endDate: filters.endDate,
        campaignIds: filters.campaignIds.join(','),
      })
      // Empty time inputs mean the whole day — the backend applies that
      // default, so an untouched field is simply omitted here.
      if (filters.startTime) params.set('startTime', filters.startTime)
      if (filters.endTime) params.set('endTime', filters.endTime)
      if (filters.teamIds.length > 0) params.set('teamIds', filters.teamIds.join(','))

      const res = await api.get<{ rows: TeamPerformanceRow[] }>(
        `/reports/conversations/team-performance?${params.toString()}`,
      )
      setRows(res.rows ?? [])
      setHasRun(true)
    } catch (err) {
      setRows([])
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, loading, error, hasRun, run, reset }
}

export interface TeamTimelineRow {
  teamId: string
  teamName: string
  openedAt: string | null
  firstRespondedAt: string | null
  resolvedAt: string | null
  responseSeconds: number | null
  resolutionSeconds: number | null
}

/**
 * Raw per-conversation rows for the Analytics view's heatmap and duration
 * charts — the aggregated report has no timestamps to draw those from.
 * Fetched lazily: pass the SAME filters the aggregated report was run with
 * only once the Analytics tab is actually opened, so switching to it the
 * first time costs one extra request instead of every report run costing two.
 */
export function useTeamPerformanceTimeline(): {
  rows: TeamTimelineRow[]
  loading: boolean
  truncated: boolean
  /** True once a fetch has completed (or failed) — guards against re-fetching on every tab toggle. */
  hasFetched: boolean
  fetch: (filters: TeamReportFilters) => Promise<void>
  reset: () => void
} {
  const [rows, setRows] = useState<TeamTimelineRow[]>([])
  const [loading, setLoading] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const fetchTimeline = useCallback(async (filters: TeamReportFilters): Promise<void> => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        timezone: filters.timezone,
        interval: filters.interval,
        startDate: filters.startDate,
        endDate: filters.endDate,
        campaignIds: filters.campaignIds.join(','),
      })
      if (filters.startTime) params.set('startTime', filters.startTime)
      if (filters.endTime) params.set('endTime', filters.endTime)
      if (filters.teamIds.length > 0) params.set('teamIds', filters.teamIds.join(','))

      const res = await api.get<{ rows: TeamTimelineRow[]; meta: { truncated: boolean } }>(
        `/reports/conversations/team-performance/timeline?${params.toString()}`,
      )
      setRows(res.rows ?? [])
      setTruncated(res.meta?.truncated ?? false)
    } catch {
      setRows([])
      setTruncated(false)
    } finally {
      setLoading(false)
      setHasFetched(true)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setTruncated(false)
    setHasFetched(false)
  }, [])

  return { rows, loading, truncated, hasFetched, fetch: fetchTimeline, reset }
}
